import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { hasRoleAnonymous, hasRoleLimitedTours } from 'common/userRoles';

import { isRenterQualifiedForListing } from 'common/householdApplication/prequalificationHelpers';

import { APPLICATION_FORM_TYPE_RENTER_PREQUAL } from 'common/householdApplication/applicationFormFields';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DISPLAY_TYPE_MULTI_UNIT,
} from 'common/listingConstants';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrCardOld from 'client/components/SrCardOld';
import SrEmoji from 'client/components/SrEmoji';
import SrPrequalCard from 'client/components/SrPrequalCard';
import SrListingPrequalCta from 'client/components/SrListingPrequalCta';
import SrApplicationButton from 'client/components/SrApplicationButton';
import SrListingQualificationsCard from 'client/components/SrListingQualificationsCard';
import SrRentersApplyingToListingAlert from 'client/components/SrRentersApplyingToListingAlert';
import { BUTTON_THEME_GREEN } from 'client/components/ButtonDeprecated';

import HouseholdActions from 'client/actions/Household';

class SrListingPrequal extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    partner: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    renterProfile: PropTypes.object,
    tenantScreeningCriteria: PropTypes.object,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    onClickRequestTour: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    householdsApplyingToListing: PropTypes.object,
  };

  static defaultProps = {
    renterProfile: {},
  };

  _onEditRenterProfile = () => {
    const { dispatch, listing } = this.props;

    dispatch(HouseholdActions.redirectToApplicationForm, {
      formType: APPLICATION_FORM_TYPE_RENTER_PREQUAL,
      prequalListingId: parseInt(listing.id, 10),
      shouldRestart: true,
    });
  };

  render() {
    const {
      listing,
      partner,
      currentUser,
      renterProfile,
      tenantScreeningCriteria,
      history,
      dispatch,
      onClickRequestTour,
      location,
      householdsApplyingToListing,
    } = this.props;

    const queryUrl = queryString.parse(location.search);
    const isSunroomListing = listing.dataSource === LISTING_DATA_SOURCE_SUNROOM;
    const isSmartApartmentListing = listing.displayType === LISTING_DISPLAY_TYPE_MULTI_UNIT;
    const partnerHasExternalApplication =
      !!partner?.experienceConfiguration &&
      !!partner.experienceConfiguration?.externalApplicationEnabled;

    const renterQualifiedForListing =
      renterProfile && isRenterQualifiedForListing(renterProfile, listing);

    const otherHouseholdRentersApplying =
      householdsApplyingToListing &&
      householdsApplyingToListing.started &&
      householdsApplyingToListing.started.total >
        householdsApplyingToListing.started.excludingCurrentHousehold;

    if ((otherHouseholdRentersApplying || queryUrl.apply) && !partnerHasExternalApplication) {
      return (
        <SrBox>
          <SrText weight="bold" font="whitney-sc">
            apply to rent this home
          </SrText>
          <SrBox height={13} />
          <SrCardOld>
            <SrBox padding="13px 20px 20px 20px">
              <SrFlex justify="between" marginBottom={17} height={70}>
                <SrBox>
                  <SrText weight="bold" color="green" size="2xl" leading="2xl">
                    Get approved <br /> within 24 hours!
                  </SrText>
                </SrBox>
                <SrBox>
                  <SrEmoji emoji="👍" big />
                </SrBox>
              </SrFlex>
              <SrApplicationButton theme={BUTTON_THEME_GREEN} listing={listing} partner={partner} />
            </SrBox>
            {listing.status === LISTING_STATUS_ACTIVE && (
              <SrRentersApplyingToListingAlert
                householdsApplyingToListing={householdsApplyingToListing}
                isExternalApp={!!partner?.experienceConfiguration?.externalApplicationEnabled}
                listing={listing}
              />
            )}
          </SrCardOld>
        </SrBox>
      );
    }

    // Prequal component for sunroom listings
    if (isSunroomListing && tenantScreeningCriteria) {
      return (
        <div className="SrListingPrequal">
          <SrFlex justify="between">
            <SrText weight="bold" font="whitney-sc">
              {renterProfile && renterProfile.isPrequalCompleted
                ? 'qualify to rent this home'
                : 'do you qualify for this home?'}
            </SrText>
            {renterProfile && renterProfile.isPrequalCompleted && (
              <SrText
                font="whitney-sc"
                color="blue300"
                weight="bold"
                cursor="pointer"
                onClick={this._onEditRenterProfile}
              >
                edit
              </SrText>
            )}
          </SrFlex>
          <SrBox height={17} />
          <SrPrequalCard
            listing={listing}
            partner={partner}
            currentUser={currentUser}
            renterProfile={renterProfile}
            tenantScreeningCriteria={tenantScreeningCriteria}
            history={history}
            dispatch={dispatch}
            householdsApplyingToListing={householdsApplyingToListing}
          />
        </div>
      );
    }

    // Don't show prequl component for anonymous users
    if (hasRoleAnonymous(currentUser.roles) || !renterProfile) {
      return null;
    }

    // Prequal component for not sunroom listings
    if (!renterProfile.isPrequalCompleted) {
      return <SrListingPrequalCta listing={listing} />;
    }

    // Prequal component for not sunroom listings after complete quiz
    return (
      <div className="SrListingPrequal">
        <SrFlex justify="between">
          <SrText font="whitney-sc" weight="bold">
            your qualifications
          </SrText>
          <SrText
            font="whitney-sc"
            color="blue300"
            weight="bold"
            cursor="pointer"
            onClick={this._onEditRenterProfile}
          >
            edit
          </SrText>
        </SrFlex>
        <div style={{ height: 15 }} />
        <SrListingQualificationsCard
          renterProfile={renterProfile}
          listing={listing}
          showQualifiedCardPrompt={renterQualifiedForListing}
          onClickScrollDownButton={onClickRequestTour}
        />
        {!isSmartApartmentListing &&
          (renterQualifiedForListing || otherHouseholdRentersApplying) &&
          !hasRoleLimitedTours(currentUser.roles) && (
            <div>
              <div style={{ height: 41 }} />
              <SrText font="whitney-sc" weight="bold">
                apply to rent now
              </SrText>
              <div style={{ height: 15 }} />
              <SrText>
                {'The Sunroom application can be used for any rental property in Texas! ' +
                  'Fill it out once and apply to as many places as you want 🤓'}
              </SrText>
              <div style={{ height: 21 }} />
              <SrApplicationButton listing={listing} partner={partner} className="blue" />
            </div>
          )}
      </div>
    );
  }
}

SrListingPrequal.displayName = 'SrListingPrequal';

export default SrListingPrequal;
